"use client"

/**
 * 出勤(位置トラッキング)・端末セッション制御のAPIヘルパー（frontend/shared/auth）。
 *
 * notification サービス(/api/notification/*)の共通エンドポイントを叩く薄いラッパ。
 * 各アプリの api クライアント(createApi の戻り値の get/post)を注入して使う。
 */

// get/post だけを要求する最小インタフェース（各アプリの ApiModule がそのまま満たす）。
export interface SessionApiClient {
  get: <T>(path: string, params?: Record<string, unknown>) => Promise<T>
  post: <T>(path: string, data: Record<string, unknown>) => Promise<T>
}

const BASE = "/api/notification"

// アプリ種別。バックエンド(api_server/common/pkg/constants の AppCast/AppDriver/AppStaff)と一致させること。
export const SESSION_APP = {
  cast: "cast",
  driver: "driver",
  staff: "staff",
} as const

export interface WorkStatus {
  working: boolean // 出勤中(位置トラッキングON)か
  isActiveDevice: boolean // この端末が稼働端末(位置送信を許可されている)か
}

export interface SessionStatus {
  isOwner: boolean // この端末が指定アプリの所有端末(締め出されていない)か
}

/** 出勤(位置トラッキング開始)。稼働端末をこの端末に確定する（後勝ち）。 */
export function attend(api: SessionApiClient, deviceId: string): Promise<{ status: string }> {
  return api.post(`${BASE}/attend`, { deviceId })
}

/** 退勤(位置トラッキング停止)。稼働端末と一致する端末のみ実際に停止する。 */
export function leave(api: SessionApiClient, deviceId: string): Promise<{ status: string }> {
  return api.post(`${BASE}/location/stop`, { deviceId })
}

/** 出勤状態と、この端末が稼働端末かを取得する（マウント時の初期化に使う）。 */
export function getWorkStatus(api: SessionApiClient, deviceId: string): Promise<WorkStatus> {
  return api.get(`${BASE}/work-status`, { deviceId })
}

/** 指定アプリの所有端末をこの端末に更新する（ログイン確定後に1回呼ぶ）。旧端末は締め出される。 */
export function claimSession(api: SessionApiClient, app: string, deviceId: string): Promise<{ status: string }> {
  return api.post(`${BASE}/session/claim`, { app, deviceId })
}

/** この端末が指定アプリの所有端末か(締め出されていないか)を確認する（SSE再接続時の再確認に使う）。 */
export function getSessionStatus(api: SessionApiClient, app: string, deviceId: string): Promise<SessionStatus> {
  return api.get(`${BASE}/session/status`, { app, deviceId })
}
